import React, {useState} from 'react'
import styled from 'styled-components'
import {
  DragDropContext,
  Droppable,
  DroppableProvidedProps,
  DropResult,
} from 'react-beautiful-dnd'
import Grid from '@material-ui/core/Grid'
import {useAttendeeVariables, useGuestVariables} from 'Event'
import {HasRules} from 'Event/attendee-rules'
import {useEditMode} from 'Event/EditModeProvider'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import {Publishable} from 'Event/Dashboard/editor/views/Published'
import {usePanelsTemplate, usePanelsUpdate} from 'Event/template/Panels'
import {PageTitle} from 'Event/template/Panels/Page'
import ResourceItem from 'Event/template/Panels/Dashboard/Resources/ResourceList/ResourceItem'
import AddResourceButton from 'Event/template/Panels/Dashboard/Resources/ResourceList/AddResourceButton'
import ResourceListConfig from 'Event/template/Panels/Dashboard/Resources/ResourceList/ResourceListConfig'
import Scheduled, {HasSchedule} from 'lib/ui/layout/Scheduled'

export interface ResourceListSettings {
  title: string
  resources: Resource[]
  cardBackgroundColor?: string
  cardBackgroundOpacity?: number
  color?: string | null
  linkColor?: string
  menuTitle?: string
  isVisible?: boolean
}

export type Resource = Publishable &
  HasRules &
  HasSchedule & {
    name: string
    filePath: string
    description?: string | null
    actionId: string | null
    isUrl?: boolean
    url?: string
    linkText?: string
  }

export default function ResourceList() {
  const isEdit = useEditMode()
  const template = usePanelsTemplate()

  const {resourceList: list} = template
  const v = useGuestVariables()
  const isEditMode = useEditMode()

  const hasResources = list.resources.length > 0
  if (!hasResources && !isEdit) {
    return null
  }

  if (isEditMode) {
    return <ConfigurableResourceList />
  }

  return (
    <div>
      <PageTitle aria-label="resources">{v(list.title)}</PageTitle>
      <ItemListing />
      <EditModeOnly>
        <StyledAddResourceButton />
      </EditModeOnly>
    </div>
  )
}

function ConfigurableResourceList() {
  const isEdit = useEditMode()
  const template = usePanelsTemplate()
  const [configVisible, setConfigVisible] = useState(false)
  const toggleListConfig = () => setConfigVisible(!configVisible)

  const {resourceList: list} = template
  const v = useAttendeeVariables()

  const hasResources = list.resources.length > 0
  if (!hasResources && !isEdit) {
    return null
  }

  return (
    <div>
      <EditModeOnly>
        <ResourceListConfig
          showing={configVisible}
          onClose={toggleListConfig}
        />
      </EditModeOnly>
      <Editable onEdit={toggleListConfig}>
        <PageTitle aria-label="resources">{v(list.title)}</PageTitle>
      </Editable>
      <ItemListing />
      <EditModeOnly>
        <StyledAddResourceButton />
      </EditModeOnly>
    </div>
  )
}

function ItemListing() {
  const isEdit = useEditMode()

  if (!isEdit)
    return (
      <Container>
        <ResourceItemList />
      </Container>
    )

  return <DroppableList />
}

function DroppableList() {
  const handleDrag = useHandleDrag()

  return (
    <DragDropContext onDragEnd={handleDrag}>
      <Droppable droppableId="drag-and-drop-resources">
        {(provided) => (
          <Container ref={provided.innerRef} {...provided.droppableProps}>
            <>
              <ResourceItemList />
              {provided.placeholder}
            </>
          </Container>
        )}
      </Droppable>
    </DragDropContext>
  )
}

function ResourceItemList() {
  const template = usePanelsTemplate()
  const {resourceList: list} = template

  return (
    <>
      {list.resources.map((resource: Resource, index: number) => (
        <Scheduled component={resource} key={index}>
          <ResourceItem
            id={`resource-item-${index}`}
            resource={resource}
            index={index}
          />
        </Scheduled>
      ))}
    </>
  )
}

const Container = React.forwardRef<
  HTMLDivElement,
  {
    className?: string
    children: React.ReactElement | React.ReactElement[]
  } & Partial<DroppableProvidedProps>
>((props, ref) => (
  <Box className={props.className} ref={ref} {...props}>
    <Grid container>{props.children}</Grid>
  </Box>
))

function useHandleDrag() {
  const template = usePanelsTemplate()
  const updateTemplate = usePanelsUpdate()

  const {resourceList: list} = template

  return (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }

    const moved = Array.from(list.resources)
    const [removed] = moved.splice(source.index, 1)
    moved.splice(destination.index, 0, removed)

    updateTemplate({
      resourceList: {
        resources: moved,
      },
    })
  }
}

const Box = styled.div`
  margin-bottom: 30px;
  width: 100%;
`

const StyledAddResourceButton = styled(AddResourceButton)`
  margin-bottom: ${(props) => props.theme.spacing[6]}!important;
  margin-top: ${(props) => props.theme.spacing[5]}!important;
`
